import React, { useState, useRef } from 'react';
import { useNavigate, Link, Navigate, useSearchParams } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { apiUrl } from '../services/api';
import { getAuthToken, isTokenExpired } from '../services/auth';
import BrandLockup from '../components/BrandLockup';
import './Login.css';

const Register = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitation = searchParams.get('invitation');
  const invitationQuery = invitation ? `?invitation=${encodeURIComponent(invitation)}` : '';
  const toast = useRef(null);

  // Already authenticated — go straight to dashboard
  const token = getAuthToken();
  if (token && !isTokenExpired(token)) {
    return <Navigate to={`/dashboard${invitationQuery}`} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Password Mismatch',
        detail: 'Passwords do not match',
        life: 4000,
      });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(apiUrl('/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: firstName, last_name: lastName, email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.current?.show({
          severity: 'error',
          summary: 'Registration Failed',
          detail: data.error || 'Could not create account',
          life: 4000,
        });
        return;
      }
      toast.current?.show({
        severity: 'success',
        summary: 'Account Created',
        detail: 'You can now sign in',
        life: 2500,
      });
      setTimeout(() => navigate(`/login${invitationQuery}`), 2500);
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Connection Error',
        detail: 'Unable to connect to server',
        life: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Toast ref={toast} position="top-right" />
      <div className="login-card">
        <div className="login-brand">
          <BrandLockup className="brand-lockup--auth" title="Rentals" />
          <div className="login-brand-copy">
            <h1>Create Account</h1>
            <p>Create your account</p>
          </div>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-row">
            <div className="login-field">
              <label htmlFor="firstName">First Name</label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First"
                required
                autoFocus
              />
            </div>
            <div className="login-field">
              <label htmlFor="lastName">Last Name</label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last"
                required
              />
            </div>
          </div>

          <div className="login-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="login-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
            />
          </div>

          <div className="login-field">
            <label htmlFor="confirm">Confirm Password</label>
            <input
              id="confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repeat your password"
              required
            />
          </div>

          <button className="login-submit" type="submit" disabled={loading}>
            {loading ? 'Creating account…' : 'Create Account'}
          </button>
        </form>

        <p className="login-switch">
          Already have an account? <Link to={`/login${invitationQuery}`}>Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
